import type { Locale } from './i18n/index';

/**
 * Minimal TMDB client.
 *
 * Called straight from the browser with a read-only v4 token. That token ships
 * in the bundle, which is fine: it grants read access to a public catalogue and
 * nothing else. Your collection is protected by the Firestore rules.
 */

export const TMDB_BASE_URL = 'https://api.themoviedb.org/3';
export const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p';

export type PosterSize = 'w154' | 'w185' | 'w342' | 'w500' | 'w780' | 'original';
export type BackdropSize = 'w300' | 'w780' | 'w1280' | 'original';

export function posterUrl(
  path: string | null | undefined,
  size: PosterSize = 'w342',
): string | null {
  return path ? `${TMDB_IMAGE_BASE_URL}/${size}${path}` : null;
}

export function backdropUrl(
  path: string | null | undefined,
  size: BackdropSize = 'w1280',
): string | null {
  return path ? `${TMDB_IMAGE_BASE_URL}/${size}${path}` : null;
}

export interface TmdbSearchResult {
  tmdbId: number;
  title: string;
  originalTitle: string | null;
  year: number | null;
  releaseDate: string | null;
  posterPath: string | null;
  overview: string | null;
}

export interface TmdbMovieDetails {
  tmdbId: number;
  title: string;
  titleDe: string | null;
  originalTitle: string | null;
  year: number | null;
  releaseDate: string | null;
  runtime: number | null;
  posterPath: string | null;
  backdropPath: string | null;
  overviewEn: string | null;
  overviewDe: string | null;
  genreIds: number[];
  directors: string[];
}

/**
 * TMDB release types. A film carries one entry per country per kind of
 * release, and the kind is what separates a cinema film from a streaming one:
 * something made for Netflix has a digital release and nothing else.
 *
 * 1 premiere · 2 theatrical (limited) · 3 theatrical · 4 digital · 5 physical
 * · 6 TV
 */
export const THEATRICAL_RELEASE_TYPES = [2, 3] as const;

export interface TmdbReleaseDates {
  results?: Array<{
    iso_3166_1?: string;
    release_dates?: Array<{ type?: number }>;
  }>;
}

/**
 * Did this actually play in a cinema, anywhere?
 *
 * A limited run counts — that is how most arthouse films are released, and
 * they get discs like anything else. What this rejects is the film that only
 * ever appeared on a streaming service, which is also the film you will never
 * find on a shelf.
 */
export function hasTheatricalRelease(payload: TmdbReleaseDates): boolean {
  return (payload.results ?? []).some((country) =>
    (country.release_dates ?? []).some(
      (entry) =>
        typeof entry.type === 'number' &&
        (THEATRICAL_RELEASE_TYPES as readonly number[]).includes(entry.type),
    ),
  );
}

export class TmdbError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'TmdbError';
    this.status = status;
  }
}

const TMDB_LANGUAGE: Record<Locale, string> = { en: 'en-US', de: 'de-DE' };

interface RawSearchResponse {
  results?: Array<{
    id: number;
    title?: string;
    original_title?: string;
    release_date?: string;
    poster_path?: string | null;
    overview?: string;
  }>;
}

interface RawMovieResponse {
  id: number;
  title?: string;
  original_title?: string;
  release_date?: string;
  runtime?: number | null;
  poster_path?: string | null;
  backdrop_path?: string | null;
  overview?: string;
  genres?: Array<{ id: number; name: string }>;
  credits?: { crew?: Array<{ job?: string; name?: string }> };
}

/** TMDB sends an empty string for an unknown date, which is not a date. */
function releaseDateOf(value: string | undefined): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function yearOf(releaseDate: string | undefined): number | null {
  if (!releaseDate) return null;
  const year = Number.parseInt(releaseDate.slice(0, 4), 10);
  return Number.isFinite(year) ? year : null;
}

function blankToNull(value: string | undefined | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

type RawResult = NonNullable<RawSearchResponse['results']>[number];

function toSearchResult(result: RawResult): TmdbSearchResult {
  return {
    tmdbId: result.id,
    title: blankToNull(result.title) ?? blankToNull(result.original_title) ?? 'Untitled',
    originalTitle: blankToNull(result.original_title),
    year: yearOf(result.release_date),
    releaseDate: releaseDateOf(result.release_date),
    posterPath: result.poster_path ?? null,
    overview: blankToNull(result.overview),
  };
}

export interface TmdbClientOptions {
  /** The v4 "API Read Access Token". Empty disables the client. */
  readToken: string;
  fetchImpl?: typeof fetch;
  baseUrl?: string;
}

export interface RequestOptions {
  signal?: AbortSignal;
}

export interface TmdbClient {
  /** False when no token is configured — the UI then offers manual entry only. */
  readonly enabled: boolean;
  searchMovies(
    query: string,
    options?: RequestOptions & { locale?: Locale },
  ): Promise<TmdbSearchResult[]>;
  getMovie(tmdbId: number, options?: RequestOptions): Promise<TmdbMovieDetails>;
  /**
   * This week's trending films, minus anything that never reached a cinema.
   * Series cannot appear: the endpoint is the film one.
   */
  trendingCinemaMovies(
    options?: RequestOptions & { locale?: Locale; limit?: number },
  ): Promise<TmdbSearchResult[]>;
}

export function createTmdbClient(options: TmdbClientOptions): TmdbClient {
  const token = options.readToken.trim();
  const baseUrl = options.baseUrl ?? TMDB_BASE_URL;
  const doFetch = options.fetchImpl ?? globalThis.fetch;

  async function request<T>(path: string, params: Record<string, string>, signal?: AbortSignal) {
    if (!token) throw new TmdbError('TMDB is not configured.');

    const url = new URL(`${baseUrl}${path}`);
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);

    let response: Response;
    try {
      response = await doFetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        ...(signal ? { signal } : {}),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new TmdbError('Could not reach TMDB.');
    }

    if (!response.ok) {
      const detail =
        response.status === 401
          ? 'TMDB rejected the token.'
          : response.status === 429
            ? 'TMDB rate limit reached. Try again in a moment.'
            : `TMDB request failed (${response.status}).`;
      throw new TmdbError(detail, response.status);
    }

    return (await response.json()) as T;
  }

  return {
    enabled: token.length > 0,

    async searchMovies(query, requestOptions = {}) {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const data = await request<RawSearchResponse>(
        '/search/movie',
        {
          query: trimmed,
          include_adult: 'false',
          language: TMDB_LANGUAGE[requestOptions.locale ?? 'en'],
          page: '1',
        },
        requestOptions.signal,
      );

      return (data.results ?? []).map(toSearchResult);
    },

    async trendingCinemaMovies(requestOptions = {}) {
      const limit = requestOptions.limit ?? 12;

      // The film endpoint, not `/trending/all` — that is where series would
      // come from, and there is no point offering one for a disc shelf.
      const data = await request<RawSearchResponse>(
        '/trending/movie/week',
        { language: TMDB_LANGUAGE[requestOptions.locale ?? 'en'] },
        requestOptions.signal,
      );

      const candidates = (data.results ?? []).map(toSearchResult);

      // Whether something played in a cinema is only in the release-dates
      // record, which is a request per film. They go out together, and a film
      // whose record cannot be read is left out rather than guessed at: this
      // is a list of suggestions, so a missing one costs nothing and a wrong
      // one is noise.
      const verdicts = await Promise.all(
        candidates.map(async (candidate) => {
          try {
            const releases = await request<TmdbReleaseDates>(
              `/movie/${candidate.tmdbId}/release_dates`,
              {},
              requestOptions.signal,
            );
            return hasTheatricalRelease(releases);
          } catch (cause) {
            if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
            return false;
          }
        }),
      );

      return candidates.filter((_, index) => verdicts[index]).slice(0, limit);
    },

    async getMovie(tmdbId, requestOptions = {}) {
      // Two passes: English is the canonical record, German fills the
      // localised title and synopsis. TMDB falls back to English when a
      // translation is missing, so the German title is only kept when it
      // actually differs.
      const [en, de] = await Promise.all([
        request<RawMovieResponse>(
          `/movie/${tmdbId}`,
          { language: 'en-US', append_to_response: 'credits' },
          requestOptions.signal,
        ),
        request<RawMovieResponse>(
          `/movie/${tmdbId}`,
          { language: 'de-DE' },
          requestOptions.signal,
        ).catch(() => null),
      ]);

      const title = blankToNull(en.title) ?? blankToNull(en.original_title) ?? 'Untitled';
      const germanTitle = blankToNull(de?.title);
      const directors = (en.credits?.crew ?? [])
        .filter((member) => member.job === 'Director')
        .map((member) => blankToNull(member.name))
        .filter((name): name is string => name !== null);

      return {
        tmdbId: en.id,
        title,
        titleDe: germanTitle && germanTitle !== title ? germanTitle : null,
        originalTitle: blankToNull(en.original_title),
        year: yearOf(en.release_date),
        releaseDate: releaseDateOf(en.release_date),
        runtime: typeof en.runtime === 'number' && en.runtime > 0 ? en.runtime : null,
        posterPath: en.poster_path ?? null,
        backdropPath: en.backdrop_path ?? null,
        overviewEn: blankToNull(en.overview),
        overviewDe: blankToNull(de?.overview),
        genreIds: (en.genres ?? []).map((genre) => genre.id),
        directors: [...new Set(directors)],
      };
    },
  };
}
